import { CommentsDialog } from "../components/dialogs/CommentsDialog";
import { ErrorAlert } from "../components/alerts";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../routes";
import { SavePrompt } from "@/components";
import { SpinButton } from "@/components/form/SpinButton";
import { useForm } from "react-hook-form";
import { useLoadingScreen, useModalDialog } from "@/hooks";
import { useNavigate } from "react-router";
import { useQualifierCompanyList } from "@/queries/company.queries";
import { useRegionsLookup } from "../scopeOfWork/hooks";
import { useSaveWelder, useWelderPicture, useWelderValidation } from "./hooks";
import { useWeldingSpecialists } from "../users/hooks";
import { Welder, WelderStatus } from "@/api/williams/welders";
import { WelderSchema } from "./WelderSchema";
import { zodResolver } from "@hookform/resolvers/zod";

export type WelderFormProps = {
  data: Welder;
};

export function WelderForm({ data }: WelderFormProps) {
  const companies = useQualifierCompanyList();
  const save = useSaveWelder();
  const navigate = useNavigate();
  const regionsLookup = useRegionsLookup();
  const specialists = useWeldingSpecialists();
  const picture = useWelderPicture(data.welderId);
  const modal = useModalDialog();
  const loader = useLoadingScreen();

  const form = useForm<Welder>({
    defaultValues: data,
    resolver: zodResolver(WelderSchema),
    reValidateMode: "onChange",
  });

  const formData = form.watch();
  const regions = formData.regions ?? [];
  const validate = useWelderValidation(form);
  const errors = form.formState.errors;
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

  const handleSelectRegions = () => {
    regionsLookup.showDialog({
      selectedRegions: regions,
      onAccept: (values) => {
        form.setValue("regions", values, { shouldDirty: true });
        form.clearErrors("regions");
      },
    });
  };

  function handleSelectCompany(id: string) {
    if (companies.data) {
      const company = companies.data.find((x) => x.companyId === id);
      form.clearErrors("company");
      form.setValue("company", company, {
        shouldDirty: true,
      });
    }
  }

  function handleSelectWeldingSpecialist(id: string) {
    if (specialists.data) {
      const specialist = specialists.data.find((x) => x.id === id);
      form.clearErrors("weldingSpecialist");
      form.setValue("weldingSpecialist", specialist, {
        shouldDirty: true,
      });
    }
  }

  function handleChangeIneligible() {
    if (formData.status === WelderStatus.Inactive)
      form.setValue("status", WelderStatus.Active, {
        shouldDirty: true,
      });
    else {
      modal.show(CommentsDialog, {
        required: true,
        caption: "Enter Reason",
        onAccept: (reason) => {
          form.setValue("welderNote", reason);
          form.setValue("ineligibleDate", new Date());
          form.setValue("status", WelderStatus.Inactive, {
            shouldDirty: true,
          });
        },
      });
    }
  }

  async function handleSave() {
    loader.show();
    if ((await validate(formData)) !== true) {
      loader.hide();
      return;
    }
    save.mutateAsync(formData, {
      onSuccess: (welder) =>
        navigate(getFullPath(routes.welder.open(welder.welderId)), {
          state: {
            skipSavePrompt: true,
          },
        }),
    });
  }

  return (
    <SavePrompt when={isDirty}>
      <h3 className="mb-2">Welder</h3>
      <div className="d-md-flex gap-4">
        <div className="d-flex flex-column align-items-md-center mt-2">
          {picture.data ? (
            <img
              src={picture.data.data}
              width="140"
              height="140"
              title="Photo"
              className="object-fit-contain border rounded"
            />
          ) : (
            <div className="border rounded" title="Photo" style={{ width: 140, height: 140 }} />
          )}
          <div className="form-check d-none d-md-block mt-2">
            <input
              type="checkbox"
              id="ineligible"
              className="form-check-input"
              checked={formData.status === WelderStatus.Inactive}
              onChange={handleChangeIneligible}
            />
            <label className="form-check-label" htmlFor="ineligible">
              Ineligible
            </label>
          </div>
        </div>
        <form
          className="flex-fill"
          autoComplete="off"
          onSubmit={form.handleSubmit(handleSave)}
          style={{ maxWidth: 450 }}
        >
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="col-form-label pb-0">
              First Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.firstName && "border-danger"}`}
              maxLength={50}
              {...form.register("firstName")}
            />
            {errors.firstName && <span className="text-danger">{errors.firstName.message}</span>}
          </div>
          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="col-form-label pb-0">
              Last Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.lastName && "border-danger"}`}
              maxLength={50}
              {...form.register("lastName")}
            />
            {errors.lastName && <span className="text-danger">{errors.lastName.message}</span>}
          </div>
          {/* Last 4 SSN */}
          <div>
            <label htmlFor="lastFourSSN" className="col-form-label pb-0">
              Last 4 SSN <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.ssn && "border-danger"}`}
              maxLength={4}
              {...form.register("ssn")}
            />
            {errors.ssn && <span className="text-danger">{errors.ssn.message}</span>}
          </div>
          {/* Welder ID */}
          <div>
            <label htmlFor="badgeNumber" className="col-form-label pb-0">
              Welder ID <span className="text-danger">*</span>
            </label>
            <input type="text" className="form-control" value={formData.lastName + formData.ssn} readOnly={true} />
            {errors.badgeNumber && <span className="text-danger">{errors.badgeNumber.message}</span>}
          </div>
          {/* Contractor */}
          <div>
            <label htmlFor="company" className="col-form-label pb-0">
              Contractor
            </label>
            <select
              className="form-select"
              value={form.getValues("company.companyId") ?? undefined}
              onChange={(e) => handleSelectCompany(e.target.value)}
            >
              <option />
              {companies.data &&
                companies.data.map((company) => (
                  <option key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </option>
                ))}
            </select>
          </div>
          {/* Email Address */}
          <div>
            <label htmlFor="emailAddress" className="col-form-label pb-0">
              Email Address
            </label>
            <input
              type="text"
              className={`form-control ${errors.emailAddress && "border-danger"}`}
              maxLength={100}
              {...form.register("emailAddress")}
            />
            {errors.emailAddress && <span className="text-danger">{errors.emailAddress.message}</span>}
          </div>
          {/* Phone Number */}
          <div>
            <label htmlFor="phoneNumber" className="col-form-label pb-0">
              Phone Number
            </label>
            <input
              type="text"
              className={`form-control ${errors.phoneNumber && "border-danger"}`}
              maxLength={16}
              {...form.register("phoneNumber")}
            />
            {errors.phoneNumber && <span className="text-danger">{errors.phoneNumber.message}</span>}
          </div>
          {/* Welding Specialistt */}
          <div>
            <label htmlFor="weldingSpecialist" className="col-form-label pb-0">
              Welding Specialist
            </label>
            <select
              className={`form-select ${errors.weldingSpecialist && "border-danger"}`}
              value={form.getValues("weldingSpecialist.id") ?? undefined}
              onChange={(e) => handleSelectWeldingSpecialist(e.target.value)}
            >
              <option />
              {specialists.data &&
                specialists.data.map((specialist) => (
                  <option key={specialist.id} value={specialist.id}>
                    {specialist.name ?? specialist.userName}
                  </option>
                ))}
            </select>
            {errors.weldingSpecialist && <span className="text-danger">{errors.weldingSpecialist.message}</span>}
          </div>
          {/* Shop or Field */}
          <div>
            <label htmlFor="shopOrField" className="col-form-label pb-0">
              Shop or Field
            </label>
            <select className={`form-select ${errors.shopField && "border-danger"}`} {...form.register("shopField")}>
              <option />
              <option>Shop</option>
              <option>Field</option>
              <option>Both</option>
            </select>
            {errors.shopField && <span className="text-danger">{errors.shopField.message}</span>}
          </div>
          {/* Regions */}
          <div>
            <label htmlFor="regions" className="col-form-label pb-0">
              Region(s)
            </label>
            <div className="input-group">
              <input
                type="text"
                className={`form-control ${errors.regions && "border-danger"}`}
                readOnly
                value={regions.map((region) => region.name).join(", ")}
              />
              <span className="input-group-text" role="button" onClick={handleSelectRegions}>
                <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
              </span>
            </div>
            {errors.regions && <span className="text-danger">{errors.regions.message}</span>}
          </div>

          <SpinButton type="submit" className="btn btn-default mt-2 mb-3" disabled={loader.visible || isDirty !== true}>
            Save
          </SpinButton>

          {errors.root && <ErrorAlert>{errors.root?.message}</ErrorAlert>}
        </form>
      </div>
    </SavePrompt>
  );
}
